import { useEffect, useState, useContext } from "react";
import ClusteringLocationOn from "../common/ClusteringLocationOn";
import ClusterMarker from "../MapMarkers/ClusterMarker";
import { Marker } from "react-map-gl";
import ClusterContext from "./ClusterContext";
import { parseAndValidateLngLat } from "../../utils/geo";

//Handles the full process for displaying a markerGroup
const MarkerGroup = ({
  restServicePost,
  markerOption,
  isMultiDestination,
  queryString,
  primaryGroupShortCode,
  setActiveDestinationEventSelectedOnMap,
  onClickCluster,
  mapBounds,
  zoom,
  apiToken,
}) => {
  const { registerMarkers, unRegisterMarkers } = useContext(ClusterContext);

  //Decides whether to use static or dynamic loading. Will attempt to load dynamically until it gets a static result from the backend
  const [usingBackendCache, setUsingBackendCache] = useState(true);

  //Static events, rendered and clustered using a clusterhandler
  const [frontClusteredMarkers, setFrontClusteredMarkers] = useState([]);

  //Dynamic clusters and events.
  const [currentDestinationEvents, setCurrentDestinationEvents] = useState([]);
  const [currentClusters, setCurrentClusters] = useState([]);

  //State to manage when/why the dynamic clusters and events update
  const [currentBounds, setCurrentBounds] = useState({});
  const [currentZoom, setCurrentZoom] = useState(0);
  const [queryOnCooldown, setQueryOnCooldown] = useState(false);
  const [queryInProgress, setQueryInProgress] = useState(false);
  const updateRateLimitMS = 500;

  useEffect(() => {
    //Whenever the query is put on cooldown, take it off cooldown the appropriate amount of time later
    if (queryOnCooldown) {
      setTimeout(() => {
        setQueryOnCooldown(false);
      }, updateRateLimitMS);

      return;
    }
  }, [queryOnCooldown]);

  useEffect(() => {
    //The conditions in which an dynamic marker load could be triggered
    if (
      queryOnCooldown ||
      queryInProgress ||
      !mapBounds ||
      !zoom ||
      !usingBackendCache
    ) {
      return;
    }

    if (
      mapBounds.minLatitude !== currentBounds.minLatitude ||
      mapBounds.minLongitude !== currentBounds.minLongitude ||
      mapBounds.maxLatitude !== currentBounds.maxLatitude ||
      mapBounds.minLatitude !== currentBounds.minLatitude
    ) {
      //Only update if a change in viewport has occured, triggering the new load
      setQueryOnCooldown(true);
      setQueryInProgress(true);
      setCurrentBounds(mapBounds);
      setCurrentZoom(zoom);
    }
  }, [
    queryOnCooldown,
    queryInProgress,
    mapBounds,
    zoom,
    currentBounds,
    currentZoom,
    usingBackendCache,
  ]);

  useEffect(() => {
    let cancelled = false;
    /*
    //TODO: currentBounds is not set before the map is moved. Re-enable this filter when it is being properly set on initial load
    if (
      !currentBounds.minLatitude ||
      !currentBounds.minLongitude ||
      !currentBounds.maxLatitude ||
      !currentBounds.maxLongitude
    ) {
      return;
    }
    */

    var markerOptionRequest = {
      sourceIds: queryString.sourceIds,
      memberId: queryString.member_id,
      categories: queryString.categories,
      analyticsRestrictDate: queryString.analyticsRestrictDate,
      analyticsRestrictEvents: queryString.analyticsRestrictEvents,
      primaryGroupShortCode: primaryGroupShortCode,
      markerOptions: markerOption,
      restrictToBox: currentBounds,
      zoomLevel: currentZoom,
      edSlug: queryString.q,
    };

    console.log(
      `Performing a cluster request for group ${markerOption.destinationGroupShortCode} with the following bounds and zoom ${currentZoom}`
    );
    console.log(currentBounds);

    restServicePost(apiToken, "getClusters", markerOptionRequest).then(
      (response) => {
        if (cancelled) return;
        var destinationGroupData = response.data.destinationGroup;
        var destinationEvents = response.data.destinationEvents;
        var mapClusters = response.data.mapClusters;
        var backendClustering = response.data.backendClustering;
        destinationEvents.forEach((destinationEvent) => {
          destinationEvent.markerDetail = {
            type: markerOption.marker.icon,
            color: markerOption.marker.color,
            activePrimaryColor: markerOption.marker.activePrimaryColor,
            activeSecondaryColor: markerOption.marker.activeSecondaryColor,
            deactivePrimaryColor: markerOption.marker.deactivePrimaryColor,
            deactiveSecondaryColor: markerOption.marker.deactiveSecondaryColor,
            name: markerOption.marker.name,
            size:
              markerOption.marker.size === undefined
                ? "2x"
                : markerOption.marker.size,
            circleActive: markerOption.marker.circleActive,
          };

          if (
            destinationGroupData.destinationSourceType !== undefined &&
            destinationGroupData.destinationSourceType === "txgb"
          ) {
            destinationEvent.isTXGBavailable = true;
          }
        });

        if (cancelled) return;
        setQueryInProgress(false);

        if (backendClustering) {
          //Is a dynamically loading group
          if (cancelled) return;
          setFrontClusteredMarkers([]);
          setCurrentDestinationEvents(destinationEvents);
          setCurrentClusters(mapClusters);
        } else {
          //Is a statically loading group. Create the markers and register them for clustering
          setCurrentDestinationEvents([]);
          setCurrentClusters([]);
          setUsingBackendCache(false);
          var markers = destinationEvents
            .filter((destinationEvent) => !destinationEvent.hideLocationMarker)
            .map((destinationEvent, index) => {
              const { lat, lng, valid } = parseAndValidateLngLat(
                destinationEvent.location.longitude,
                destinationEvent.location.latitude
              );
              if (!valid) return null;

              return (
                <Marker
                  key={"FrontClustered" + index}
                  latitude={lat}
                  longitude={lng}
                  offsetLeft={-20}
                  offsetTop={-40}
                >
                  <ClusteringLocationOn
                    destinationEvent={destinationEvent}
                    onClick={() =>
                      setActiveDestinationEventSelectedOnMap(destinationEvent)
                    }
                  />
                </Marker>
              );
            });

          if (cancelled) return;
          setFrontClusteredMarkers(markers.filter(Boolean));
        }
      }
    );

    return () => {
      cancelled = true;
    };
  }, [
    restServicePost,
    primaryGroupShortCode,
    isMultiDestination,
    markerOption,
    queryString,
    currentBounds,
    currentZoom,
    setActiveDestinationEventSelectedOnMap,
    apiToken,
  ]);

  useEffect(() => {
    if (!usingBackendCache) {
      registerMarkers(frontClusteredMarkers);
      return () => {
        //Unregister the markers on unmount
        unRegisterMarkers(frontClusteredMarkers);
      };
    }
  }, [
    usingBackendCache,
    registerMarkers,
    unRegisterMarkers,
    frontClusteredMarkers,
  ]);

  return (
    <>
      {currentClusters.map((cluster, index) => (
        <ClusterMarker
          key={"ClusterMarker" + index}
          onClick={() => {
            onClickCluster({
              zoom: Math.max(...cluster.zoomLevels) + 1,
              longitude: cluster.location.coordinates.x,
              latitude: cluster.location.coordinates.y,
            });
          }}
          latitude={parseFloat(cluster.location.coordinates.y)}
          longitude={parseFloat(cluster.location.coordinates.x)}
          pointCount={cluster.count}
        />
      ))}
      {currentDestinationEvents.map((destinationEvent, index) => {
        const { lat, lng, valid } = parseAndValidateLngLat(
          destinationEvent.location.longitude,
          destinationEvent.location.latitude
        );
        if (!valid) return null;
        return (
          <Marker
            key={"BackClustered" + index}
            latitude={lat}
            longitude={lng}
            offsetLeft={-20}
            offsetTop={-40}
            clusterMe={true}
          >
            <ClusteringLocationOn
              destinationEvent={destinationEvent}
              onClick={() =>
                setActiveDestinationEventSelectedOnMap(destinationEvent)
              }
            />
          </Marker>
        );
      })}
    </>
  );
};

export default MarkerGroup;
